import { KeycloakInstance } from 'keycloak-js';
import { useState, useEffect, useRef, useCallback } from 'react';
import { Form, Input, Select, Button, Alert, Spin } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import getQueryString from 'common/utils/getQueryStrings';
import DwnKeycloak, { JUMP_GRANT, JUMP_TOKEN_KEY, LocalGrant, CUSTOMER_TOKEN_KEY } from 'common/auth/DwnKeycloak';
import { fetchJson } from 'common/utils/fetchJson';
import DwnStash, { RealmData, State } from 'common/stash/DwnStash';
import { BroadcastChannel } from 'broadcast-channel';
import { NewPassword, TokenPayload } from './NewPassword';

const LoginForm = () => {
  const [customerIds, setCustomerIds] = useState<string[]>([]);
  const [realmData, setRealmData] = useState<RealmData>();
  const [isLoading, setIsLoading] = useState(true);
  const [isJumpAuthenticated, setIsJumpAuthenticated] = useState(false);
  const [isSetPassword, setIsSetPassword] = useState(false);
  const [tokenPayload, setTokenPayload] = useState<TokenPayload>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'error' | 'info' }>();
  const [fatalError, setFatalError] = useState<string>();

  const [emailForm] = Form.useForm();
  const [orgForm] = Form.useForm();

  const kcJumpRef = useRef<KeycloakInstance>(DwnKeycloak.getKeycloak('dwn-jump'));
  const jumpGrantRef = useRef<LocalGrant | undefined>(undefined);
  const jumpInitRef = useRef<Promise<unknown> | undefined>(undefined);
  const channelRef = useRef<BroadcastChannel<{ type: string }>>(
    new BroadcastChannel('auth', { webWorkerSupport: false }),
  );
  const hasInitializedRef = useRef(false);

  const next = window.location.origin + '/';

  // Parse query params once on mount - these values are stable for the component lifetime
  const queries = useRef(getQueryString()).current;
  const autoLoginDomain = queries['domain'];
  const isAuthRequired = queries['required'] !== undefined;

  useEffect(() => {
    const passwordResetMode =
      queries['token'] !== undefined &&
      queries['exp'] !== undefined &&
      queries['type'] !== undefined &&
      queries['email'] !== undefined;

    setIsSetPassword(passwordResetMode);

    if (passwordResetMode) {
      setTokenPayload({
        token: queries['token'],
        exp: queries['exp'],
        type: queries['type'],
        email: queries['email'],
      });
      setIsLoading(false);
    }
  }, [queries]);

  // Logging into an actual customer org simply requires some localStorage keys to be set followed by a redirect to the portal homepage
  // The actual login is handled by the AuthWrapper around the main app
  const doLogin = useCallback(
    (customerId: string) => {
      DwnKeycloak.updateStore({ customerId });
      DwnKeycloak.setGrant(JUMP_GRANT, jumpGrantRef.current!);
      window.location.replace(next);
    },
    [next],
  );

  // Auto sso jump login with prefilled domain name
  const ssoSubmitForm = useCallback(async () => {
    try {
      const data = await fetchJson('../api/email', {
        method: 'POST',
        // The email is a placeholder, only the domain matters
        body: JSON.stringify({ login: `_auto_@${autoLoginDomain!}` }),
      });
      if (data.sso) {
        kcJumpRef.current.login({
          redirectUri: next + 'auth?jump',
          idpHint: data.sso,
        });
      } else {
        // The provided auto login domain does not map to a valid idp, redirect back to the regular login flow
        window.location.href = next + 'auth';
      }
    } catch (e) {
      setIsSubmitting(false);
      setIsLoading(false);
      setMessage({ text: 'Failed to connect, please try again!', type: 'error' });
    }
  }, [autoLoginDomain, next]);

  useEffect(() => {
    // Prevent double initialization in React StrictMode
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;

    const initializeAuth = async () => {
      const kcJump = kcJumpRef.current;
      const channel = channelRef.current;

      // If another tab logs in, redirect to post-login url automatically
      channel.onmessage = (msg) => {
        console.log('broadcast', msg);
        switch (msg.type) {
          case 'loggedIn':
            window.location.href = next;
            break;
        }
      };

      // If the jump token expires during the login step, immediately kill the session and make the user start over again
      kcJump.onTokenExpired = () => {
        console.log('jump token expired, logging out');
        DwnKeycloak.clearLoginStores();
        kcJump.logout({
          redirectUri: next + 'auth',
        });
      };

      // Init keycloak and save the promise so we can check it before the form is submitted
      jumpInitRef.current = DwnKeycloak.safeInit(kcJump, { pkceMethod: 'S256' }).catch((err) => {
        console.log(err);
        setFatalError(`Error: An unexpected error has occured.`);
      });

      await jumpInitRef.current;
      console.log('init', kcJump, kcJump.authenticated);

      if (kcJump.authenticated) {
        setIsSetPassword(false);
        DwnKeycloak.clearLoginStores();
        DwnKeycloak.setTokenCookie(JUMP_TOKEN_KEY, kcJump.token!);
        jumpGrantRef.current = DwnKeycloak.deriveGrant(kcJump);
        const permissions = kcJump!.tokenParsed!.resource_access;
        console.log(
          'logged into jump realm',
          kcJump!.realm,
          'with grant',
          jumpGrantRef.current,
          'and permissions',
          permissions,
        );

        if (permissions) {
          const email = kcJump!.tokenParsed!.email;
          DwnKeycloak.updateStore({ email: email });

          // See which customerIds we can jump to
          const access: string[] = [];
          for (let [key, value] of Object.entries(permissions)) {
            if (key.startsWith('jump-')) {
              const customerName = key.substring(5);
              if (customerName.length > 0 && value.roles.find((role) => role === 'access')) {
                access.push(customerName);
              }
            }
          }
          console.log(access);

          if (access.length === 0) {
            setFatalError('Error: you have no access to any customer orgs.');
            return;
          } else {
            // Retrieve realm metadata (ie customer name)
            let fetchedRealmData: RealmData | undefined = DwnStash.get(State.REALMS);
            if (!fetchedRealmData) {
              try {
                fetchedRealmData = await fetchJson('../api/internal/realms', {
                  method: 'GET',
                });
              } catch (e) {
                setFatalError(`Error: ${e}`);
                return;
              }
              DwnStash.set(State.REALMS, fetchedRealmData);
            }
            console.log(fetchedRealmData);

            // The list of realms the token claims we have access to does not match what we got from the API
            // which should never happen in theory, because the API re-uses the same data from the same token
            if (access.some((customerId) => !fetchedRealmData![customerId])) {
              DwnStash.set(State.REALMS, undefined);
              setFatalError(`Error: An unknown error has occured.`);
              return;
            }

            // Try and autologin to customer org or else show realm selector form
            access.sort((a, b) => fetchedRealmData![a].customerName.localeCompare(fetchedRealmData![b].customerName));
            setCustomerIds(access);
            setRealmData(fetchedRealmData);
            orgForm.setFieldsValue({ customerIds: access[0] });

            if (access.length === 1) {
              // If there's only one Customer Org to login to, automatically log into it
              console.log('attempting autologin', access[0]);
              doLogin(access[0]);
            } else {
              setIsLoading(false);
            }
          }
        } else {
          setFatalError('Error: you have no access to any customer orgs.');
          return;
        }
        console.log('jump state is still', kcJump);
      } else {
        if (isAuthRequired) {
          DwnKeycloak.clearLoginStores();
        }
        if (!isAuthRequired && DwnKeycloak.getTokenCookie(CUSTOMER_TOKEN_KEY)) {
          // We appear to be already logged in, redirect back to main aphex portal
          console.log('Already logged in, redirecting');
          window.location.replace(next);
        } else if (!isAuthRequired && DwnKeycloak.getTokenCookie(JUMP_TOKEN_KEY)) {
          // We appear to be logged into the jump realm but not any customer realms yet
          console.log('Logged into jump realm only, authenticating in');
          kcJump.login({
            redirectUri: next + 'auth?jump',
          });
        } else {
          // If a domain param is present and the domain looks valid, attempt an auto sso jump login to that domain
          if (autoLoginDomain && /^([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}$/.test(autoLoginDomain)) {
            console.log('auto logging in with domain', autoLoginDomain);
            ssoSubmitForm();
          } else {
            // Otherwise continue to regular login flow
            setIsLoading(false);
          }
        }
      }

      setIsJumpAuthenticated(!!kcJump.authenticated);
    };

    initializeAuth();

    const channel = channelRef.current;
    return () => {
      channel?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Login to selected customer org
  const submitJump = useCallback(
    async (values: { customerIds: string }) => {
      if (!values.customerIds || customerIds.length === 0) return;
      if (!customerIds.includes(values.customerIds)) {
        return;
      }
      console.log('attempting selector login', values.customerIds);
      doLogin(values.customerIds);
      setIsLoading(true);
    },
    [customerIds, doLogin],
  );

  // Jump login with user provided email address
  const submitForm = useCallback(
    async (values: { login: string }) => {
      setIsSubmitting(true);
      setMessage(undefined);

      try {
        const data = await fetchJson('../api/email', {
          method: 'POST',
          body: JSON.stringify(values),
        });
        await jumpInitRef.current;
        kcJumpRef.current.login({
          redirectUri: next + 'auth?jump',
          loginHint: values.login,
          idpHint: data.sso,
        });
      } catch (e) {
        setIsSubmitting(false);
        setMessage({ text: 'Failed to connect, please try again!', type: 'error' });
      }
    },
    [next],
  );

  return (
    <div>
      {fatalError ? (
        <Alert message={fatalError} type="error" showIcon />
      ) : isLoading ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
        </div>
      ) : isJumpAuthenticated ? (
        <Form form={orgForm} onFinish={submitJump} layout="vertical">
          <Form.Item
            label="Select Customer Org"
            name="customerIds"
            rules={[{ required: true, message: 'Please select a customer org' }]}
          >
            <Select
              showSearch
              autoFocus
              placeholder="Select a customer organization"
              optionFilterProp="children"
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              options={customerIds.map((customerId) => {
                const realm = realmData![customerId];
                const name = realm.displayName || realm.customerName;
                return {
                  value: customerId,
                  label: `${name} - (Customer ID: ${customerId})`,
                };
              })}
              size="large"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  orgForm.submit();
                }
              }}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" disabled={isSubmitting} block size="large">
              Next
            </Button>
          </Form.Item>
        </Form>
      ) : !isSetPassword ? (
        <Form form={emailForm} onFinish={submitForm} layout="vertical">
          <Form.Item
            label="Email Address"
            name="login"
            rules={[
              { required: true, message: 'Please enter your email' },
              { type: 'email', message: 'Please enter a valid email' },
            ]}
          >
            <Input prefix={<MailOutlined />} placeholder="Enter your email address" autoFocus size="large" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" disabled={isSubmitting} block size="large">
              Next
            </Button>
          </Form.Item>
        </Form>
      ) : (
        <NewPassword payload={tokenPayload!} />
      )}
      {message && (
        <Alert
          message={isSubmitting ? 'Submitting...' : message.text}
          type={message.type}
          showIcon
          style={{ marginTop: 16 }}
        />
      )}
    </div>
  );
};

export default LoginForm;
